import React, { useEffect, useState } from 'react';
import { Grid, MenuItem, Typography } from '@mui/material';
import {
    columns,
    daysOfMessagesDropdownData,
    SystemMessageData,
    styles,
} from './system-messages-helper';
import { DataGrid, SearchField, Select } from '@biq/apex-ui';
import { ChainSvg } from '../../assets/images';
import { useSystemMessages } from '../../hooks';
import { HelixLoader } from '../../components';
import { DetailsDrawer } from './components/DetailsDrawer';
import { CustomIconButton } from 'components/custom-icon-button/CustomIconButton';

export const SystemMessages = (): React.JSX.Element => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [messsagesData, setMesssagesData] = useState<SystemMessageData[]>([]);
    const [searchedData, setSearchedData] = useState<SystemMessageData[]>([]);
    const [daysOfMessages, setDaysOfMessages] = useState<string>('15');
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [selectedMessageId, setSelectedMessageId] = useState<string>('');
    const {
        data: messagesListResponse,
        isLoading,
        isFetching,
        refetch,
    } = useSystemMessages({
        daysToReturn: daysOfMessages,
    });

    useEffect(() => {
        if (!isLoading && !isFetching && messagesListResponse) {
            setMesssagesData(messagesListResponse.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    useEffect(() => {
        if (searchValue) {
            setIsSearched(true);
            const initialData =
                messsagesData.filter((data) =>
                    data.title.toLowerCase().includes(searchValue.toLowerCase())
                ) || [];
            setSearchedData(initialData);
        } else {
            setIsSearched(false);
            setSearchedData(messsagesData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const handleSearchFieldClear = (): void => {
        setSearchValue('');
        setIsSearched(false);
        setSearchedData(messsagesData);
    };

    const handleRefreshButtonClick = (): void => {
        refetch();
        setDaysOfMessages('15');
        setSearchValue('');
        setIsSearched(false);
        setSearchedData(messsagesData);
    };

    const handleDaysOfMessagesSelected = (days: string): void => {
        setDaysOfMessages(days);
        refetch();
    };

    const handleDrawerClose = (): void => {
        setDrawerOpen(false);
        setSearchValue('');
        setIsSearched(false);
        setSearchedData(messsagesData);
    };

    const handleDrawerOpen = (id: string): void => {
        setDrawerOpen(true);
        setSelectedMessageId(id);
    };

    const handleSearchField = (value: string): void => {
        setSearchValue(value);
    };

    const rowHeight = 52;
    const maxGridHeight = `calc(100vh - 310px)`;

    const gridHeight = Math.min(
        (isSearched ? searchedData.length : messsagesData.length) * rowHeight +
            113,
        window.innerHeight - 310
    );

    const isEmpty = isSearched
        ? searchedData.length === 0
        : messsagesData.length === 0;

    let content;
    if (isLoading || isFetching) {
        content = <HelixLoader />;
    } else if (isEmpty) {
        content = (
            <Grid sx={styles.emptyStateContainerStyles}>
                <Grid>
                    <img src={ChainSvg} alt='Empty_State_Image' />
                </Grid>
                <Grid mt={4}>
                    <Typography sx={styles.emptyHeadingTextStyles}>
                        Nothing to see here.
                    </Typography>
                </Grid>
                <Grid mt={2}>
                    <Typography sx={styles.emptySubHeadingTextStyles}>
                        There are no system updates in the specified timeframe.
                        Update date range or search criteria.
                    </Typography>
                </Grid>
            </Grid>
        );
    } else {
        content = (
            <Grid
                mt={'20px'}
                sx={{
                    height: gridHeight,
                    maxHeight: maxGridHeight,
                }}
            >
                <DataGrid
                    variantType='outline'
                    autoHeight={false}
                    rows={isSearched ? searchedData : messsagesData}
                    columns={columns}
                    getRowId={(row: any): string => row.messageId.toString()}
                    disableRowSelectionOnClick
                    onRowClick={(dataGridProMap: any): void => {
                        handleDrawerOpen(dataGridProMap?.row?.messageId);
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 25,
                            },
                        },
                    }}
                    pageSizeOptions={[25, 50, 100]}
                    pagination
                />
            </Grid>
        );
    }

    return (
        <>
            <Grid sx={styles.mainContainer}>
                <Grid sx={styles.titleContainerStyles}>
                    <Typography sx={styles.headingTextStyles}>
                        System Alerts
                    </Typography>
                    <CustomIconButton
                        className='refresh-icon'
                        title='Refresh Page'
                        icon='ic:baseline-refresh'
                        onClick={handleRefreshButtonClick}
                    />
                </Grid>
                <Grid sx={styles.searchContainerStyles}>
                    <Grid>
                        <SearchField
                            value={searchValue}
                            onChange={(e): void => {
                                handleSearchField(e.target.value);
                            }}
                            variant='outlined'
                            placeholder='Search...'
                            size='medium'
                            onClear={handleSearchFieldClear}
                            sx={styles.searchFieldStyles}
                        />
                    </Grid>
                    <Grid>
                        <Select
                            value={daysOfMessages}
                            sx={styles.selectFieldStyles}
                        >
                            {daysOfMessagesDropdownData.map((day) => (
                                <MenuItem
                                    key={day.daysValue}
                                    value={day.daysValue}
                                    onClick={(): void =>
                                        handleDaysOfMessagesSelected(
                                            day.daysValue
                                        )
                                    }
                                >
                                    {day.daysName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                </Grid>
                {content}
            </Grid>
            {drawerOpen && (
                <DetailsDrawer
                    drawerOpen={drawerOpen}
                    onDrawerClose={handleDrawerClose}
                    selectedMessageId={selectedMessageId}
                />
            )}
        </>
    );
};
